import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Col, Row } from '@enuygun/design-system'
import NextImage from 'components/Image'
import { useTranslation } from 'next-i18next'
import SnapCarousel from 'components/SnapCarousel'
import { useIsMobile } from 'hooks/useIsMobile'
import { useIsClient } from 'hooks/useIsClient'
import { DiscoverData } from './data'
import * as S from './Discover.styled'
import { useAppState } from 'context/AppContext'
import sendTrackerLog from 'utils/tracker'
import { getAnalyticsRequestId } from '../../utils/analyticsRequestId'
import { useDeviceType } from 'hooks/useDeviceType'

interface DiscoverCardProps {
  place: string
  bg: string
  flightUrl: string
  HotelUrl: string
  BusUrl?: string
  CarUrl?: string
  TravelUrl: string
}

const LazyDiscoverSectionDesktop: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  // SSR için true ile başlatıyoruz, böylece JavaScript kapalıyken de içerik render edilir
  const [isVisible, setIsVisible] = useState(true)
  const [isClient, setIsClient] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsClient(true)

    if (globalThis.window === undefined || !('IntersectionObserver' in globalThis.window)) {
      setIsVisible(true)
      return
    }

    const currentElement = sectionRef.current
    if (!currentElement) return

    setIsVisible(false)

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.unobserve(currentElement)
        }
      },
      {
        rootMargin: '300px',
        threshold: 0.01,
      }
    )

    const timeoutId = setTimeout(() => {
      if (currentElement) {
        observer.observe(currentElement)
      }
    }, 150)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [])

  const shouldShowContent = !isClient || isVisible

  return (
    <>
      <style jsx>{`
        @keyframes discoverSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .discover-spinner {
          animation: discoverSpin 1s linear infinite;
        }
      `}</style>

      <div
        ref={sectionRef}
        data-testid="lazy-discover-section-desktop"
        data-visible={shouldShowContent ? 'true' : 'false'}
        style={{
          minHeight: shouldShowContent ? 'auto' : '400px',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {shouldShowContent ? (
          children
        ) : (
          <div
            style={{
              width: '100%',
              height: '400px',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              border: '1px solid #e9ecef',
              margin: '20px 0',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                className="discover-spinner"
                style={{
                  width: '36px',
                  height: '36px',
                  border: '3px solid #e9ecef',
                  borderTop: '3px solid #28a745',
                  borderRadius: '50%',
                }}
              />
              <div
                style={{
                  color: '#28a745',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                ...
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const LazyDiscoverCardMobile: React.FC<{
  children: React.ReactNode
  index: number
}> = ({ children, index }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isClient, setIsClient] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsClient(true)

    if (globalThis.window === undefined || !('IntersectionObserver' in globalThis.window)) {
      setIsVisible(true)
      return
    }

    const currentElement = cardRef.current
    if (!currentElement) return

    setIsVisible(false)

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.unobserve(currentElement)
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.2,
      }
    )

    const timeoutId = setTimeout(() => {
      if (currentElement) {
        observer.observe(currentElement)
      }
    }, 50)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [])

  const shouldShowContent = !isClient || isVisible

  return (
    <div
      ref={cardRef}
      data-testid={`lazy-discover-card-mobile-${index}`}
      data-visible={shouldShowContent ? 'true' : 'false'}
      style={{
        minHeight: shouldShowContent ? 'auto' : '200px',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      {shouldShowContent ? (
        children
      ) : (
        <div
          style={{
            width: '100%',
            height: '200px',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              border: '2px solid #e9ecef',
              borderTop: '2px solid #28a745',
              borderRadius: '50%',
              animation: 'discoverSpin 1s linear infinite',
            }}
          />
        </div>
      )}
    </div>
  )
}

interface DiscoverContentProps {
  isMobile: boolean
  isTablet: boolean
  enableLazyLoading: boolean
  heading: string
  renderDiscoverCard: (data: DiscoverCardProps, key: number) => React.ReactNode
}

const DiscoverContent: React.FC<DiscoverContentProps> = ({ isMobile, isTablet, enableLazyLoading, heading, renderDiscoverCard }) => (
  <div style={{ marginRight: '0px' }}>
    <S.DiscoverHeading data-testid="discover-heading-text">{heading}</S.DiscoverHeading>
    {isMobile ? (
      <SnapCarousel
        options={{
          perPage: isTablet ? 3.25 : 1.32,
          gap: 16,
        }}
        dataTestId="discover-carousel"
        containerWidth={1120}
        isMobile={true}
      >
        {DiscoverData.map((data, index) => {
          const cardData = data as DiscoverCardProps
          return enableLazyLoading ? (
            <LazyDiscoverCardMobile key={cardData.place} index={index}>
              {renderDiscoverCard(cardData, index)}
            </LazyDiscoverCardMobile>
          ) : (
            <div key={cardData.place}>{renderDiscoverCard(cardData, index)}</div>
          )
        })}
      </SnapCarousel>
    ) : (
      <S.DiscoverWrapper gutter={[8, 16]} as={Row} data-testid="discover-wrapper">
        {DiscoverData.map((data, index) => {
          const cardData = data as DiscoverCardProps
          return (
            <Col key={cardData.place} data-testid={`discover-card-desktop-${index}`}>
              {renderDiscoverCard(cardData, index)}
            </Col>
          )
        })}
      </S.DiscoverWrapper>
    )}
  </div>
)

const Discover = () => {
  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const { t } = useTranslation('common')
  const isClient = useIsClient()
  const { market } = useAppState()

  const enableLazyLoading = isClient

  // Discover link click tracking
  const handleDiscoverLinkClick = useCallback(
    (place: string, category: string, url: string, index: number) => {
      const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: 'enuygun-homepage',
        page: 'enuygun-homepage',
        type: 'click',
        message: 'discover_card_link_click',
        environment: environment,
        requestId,
        place: place,
        linkCategory: category,
        linkUrl: url,
        cardOrder: index + 1,
        isMobile: !!isMobile,
        market: market,
      })
    },
    [isMobile, market]
  )

  const renderDiscoverCard = ({ place, bg, flightUrl, HotelUrl, BusUrl, CarUrl, TravelUrl }: DiscoverCardProps, key: number) => (
    <S.DiscoverCard key={`${key}-${JSON.stringify(isMobile)}`} data-testid={`discover-card-${key}`}>
      <S.DiscoverCardImageLink data-testid={`discover-card-image-container-${key}`}>
        <div>
          <NextImage unoptimized src={bg} fill alt={place} loading="lazy" data-testid={`discover-card-image-${key}`} />
        </div>
      </S.DiscoverCardImageLink>
      <S.DiscoverCardCategory data-testid={`discover-card-category-${key}`}>
        <S.DiscoverCardTitle data-testid={`discover-card-title-${key}`}>{place}</S.DiscoverCardTitle>
        <S.DiscoverCardElementWrapper data-testid={`discover-card-element-wrapper-${key}`}>
          {flightUrl && (
            <S.DiscoverCardCategoryElement data-testid={`discover-card-category-flight-${key}`}>
              <S.DiscoverCardElementLink
                title={`${place} Uçak Bileti`}
                href={flightUrl}
                data-testid={`discover-card-flight-link-${key}`}
                onClick={() => handleDiscoverLinkClick(place, 'flight', flightUrl, key)}
              >
                Uçak
              </S.DiscoverCardElementLink>
            </S.DiscoverCardCategoryElement>
          )}
          {HotelUrl && (
            <S.DiscoverCardCategoryElement data-testid={`discover-card-category-hotel-${key}`}>
              <S.DiscoverCardElementLink
                title={`${place} Otel Rezervasyonu`}
                href={HotelUrl}
                data-testid={`discover-card-hotel-link-${key}`}
                onClick={() => handleDiscoverLinkClick(place, 'hotel', HotelUrl, key)}
              >
                Otel
              </S.DiscoverCardElementLink>
            </S.DiscoverCardCategoryElement>
          )}
          {BusUrl && (
            <S.DiscoverCardCategoryElement data-testid={`discover-card-category-bus-${key}`}>
              <S.DiscoverCardElementLink
                title={`${place} Otobüs Bileti`}
                href={BusUrl}
                data-testid={`discover-card-bus-link-${key}`}
                onClick={() => handleDiscoverLinkClick(place, 'bus', BusUrl, key)}
              >
                Otobüs
              </S.DiscoverCardElementLink>
            </S.DiscoverCardCategoryElement>
          )}
          {CarUrl && (
            <S.DiscoverCardCategoryElement data-testid={`discover-card-category-car-${key}`}>
              <S.DiscoverCardElementLink
                title={`${place} Araç Kiralama`}
                href={CarUrl}
                data-testid={`discover-card-car-link-${key}`}
                onClick={() => handleDiscoverLinkClick(place, 'car', CarUrl, key)}
              >
                Araç
              </S.DiscoverCardElementLink>
            </S.DiscoverCardCategoryElement>
          )}
          {TravelUrl && (
            <S.DiscoverCardCategoryElement data-testid={`discover-card-category-travel-${key}`}>
              <S.DiscoverCardElementLink
                title={`${place} Gezilecek Yerler`}
                href={TravelUrl}
                data-testid={`discover-card-travel-link-${key}`}
                onClick={() => handleDiscoverLinkClick(place, 'travel', TravelUrl, key)}
              >
                {' '}
                Gezi
              </S.DiscoverCardElementLink>
            </S.DiscoverCardCategoryElement>
          )}
        </S.DiscoverCardElementWrapper>
      </S.DiscoverCardCategory>
    </S.DiscoverCard>
  )

  if (!DiscoverData || DiscoverData.length === 0) {
    return null
  }

  const content = (
    <DiscoverContent isMobile={!!isMobile} isTablet={!!isTablet} enableLazyLoading={enableLazyLoading} heading={t('discover.heading')} renderDiscoverCard={renderDiscoverCard} />
  )

  if (isMobile || !enableLazyLoading) {
    return content
  }

  return <LazyDiscoverSectionDesktop>{content}</LazyDiscoverSectionDesktop>
}

export default Discover
